import { useState } from "react";
import api from "@/lib/api";
import { V2 } from "./tokens";
import { Eyebrow } from "./V2Shared";

/* Daily Brief Finale. Desktop (47:2 y6420) approved row band kept at xl.
   Phase 3 touch (58:2 / 60:2): display-scale title, side-by-side input +
   204px button at 768, stacked full-width form at 390 — all targets >=44px.
   Real signup endpoint with truthful success / validation / rate-limit states. */
export const DailyBriefFinale = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    try {
      const r = await api.post("/newsletters/subscribe", {
        email: email.trim(), source: "homepage_v2_finale", consent: true,
      });
      setMsg(r.data?.message || "You're on the list. First brief lands tomorrow morning.");
      setState("done");
    } catch (err: any) {
      setMsg(err?.response?.status === 429
        ? "Too many attempts — please try again in a minute."
        : err?.response?.data?.detail || "That email didn't validate. Check it and try again.");
      setState("error");
    }
  };

  return (
    <section id="daily-brief" data-testid="v2-daily-brief"
      className="mt-[56px] xl:py-[30px] xl:border-y xl:border-[#242432]">
      <div className="flex flex-col xl:flex-row xl:items-center gap-[18px] xl:gap-[22px]">
        <div className="min-w-0 xl:max-w-[560px]">
          <div className="xl:hidden"><Eyebrow color={V2.green2} size={10}>THE DAILY BRIEF</Eyebrow></div>
          <div className="hidden xl:block"><Eyebrow color={V2.cyan2} size={11}>THE DAILY BRIEF</Eyebrow></div>
          <h2 className="nl-sg text-[29px] leading-[34px] md:text-[30px] md:leading-[36px] xl:text-[28px] xl:leading-[34px] font-medium mt-[10px]" style={{ color: V2.text }}>
            <span className="md:hidden">The onchain economy in five minutes.</span>
            <span className="hidden md:inline">Start tomorrow already knowing what mattered overnight.</span>
          </h2>
          <p className="nl-geist text-[12px] leading-[18px] md:text-[14px] md:leading-[20px] xl:leading-[22px] mt-[8px]" style={{ color: V2.body }}>
            <span className="md:hidden">Markets, regulation, DeFi and the stories that matter — delivered with signal, not noise.</span>
            <span className="hidden md:inline">Markets · Protocols · Policy · Original NewsLayer context</span>
          </p>
        </div>
        <div aria-hidden className="hidden xl:flex items-end gap-[5px] h-[38px]">
          {[14, 26, 18, 34, 22, 38, 16, 30, 24, 36, 20, 28].map((h, i) => (
            <span key={i} className="w-[3px] rounded-full"
              style={{ height: h, background: i % 3 === 0 ? V2.violet : i % 3 === 1 ? V2.cyan2 : V2.border }} />
          ))}
        </div>
        {state === "done" ? (
          <p className="nl-geist text-[14px] leading-[22px] xl:ml-auto" style={{ color: V2.green2 }}
            data-testid="v2-daily-brief-success" role="status">
            {msg}
          </p>
        ) : (
          <form onSubmit={submit} className="flex flex-col md:flex-row gap-[10px] xl:ml-auto w-full xl:w-auto mt-[4px] xl:mt-0">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" aria-label="Email address"
              data-testid="v2-daily-brief-email"
              className="nl-geist h-[48px] md:h-[52px] xl:h-[46px] w-full md:flex-1 xl:flex-none xl:w-[300px] rounded-[10px] md:rounded-[12px] xl:rounded-full px-[16px] xl:px-[20px] text-[14px] text-white outline-none focus:ring-2 focus:ring-[#8b5cff]"
              style={{ background: V2.panelDeep, border: `1px solid ${V2.border}` }} />
            <button type="submit" disabled={state === "busy"} data-testid="v2-daily-brief-submit"
              className="nl-geist h-[50px] md:h-[52px] xl:h-[46px] w-full md:w-[204px] xl:w-auto px-[26px] rounded-[10px] md:rounded-[12px] xl:rounded-full text-[13px] md:text-[14px] xl:text-[13px] font-semibold xl:font-bold text-white shrink-0 transition-transform duration-200 hover:-translate-y-[2px] disabled:opacity-60 bg-[#8b5cff] xl:bg-[#6f2cf4]">
              {state === "busy" ? "SUBSCRIBING…" : (
                <>
                  <span className="md:hidden">Subscribe to The Daily Brief</span>
                  <span className="hidden md:inline xl:hidden">Subscribe</span>
                  <span className="hidden xl:inline">SUBSCRIBE</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
      {state === "error" && (
        <p className="nl-geist text-[13px] leading-[19px] mt-[10px] xl:text-right" style={{ color: V2.neg }}
          data-testid="v2-daily-brief-error" role="alert">
          {msg}
        </p>
      )}
    </section>
  );
};
